/**
 * Get dashboard jobs with application counts using Edge Function
 * Returns only jobs posted by current user
 */

import { supabase } from '../../../lib/supabaseClient';
import { ApiResponse } from '../../../types/api';
import { Job } from '../../../types';
import { DashboardJob, CreateJobPayload } from '../types';
import { handleEdgeFunctionError } from '../../../utils/edgeFunctionUtils';

export async function getDashboardJobsWithEdgeFunction(): Promise<ApiResponse<DashboardJob[]>> {
  try {
    const { data, error } = await supabase.functions.invoke('get-dashboard-jobs', {
      body: {},
    });

    if (error) {
      console.error('[Edge Function Error] getDashboardJobs:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: [], error: extractedError };
    }

    return { data: data?.data || [], error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] getDashboardJobs:', err);
    return { data: [], error: err };
  }
}

export async function createJobWithEdgeFunction(jobData: CreateJobPayload): Promise<ApiResponse<Job>> {
  try {
    const { data, error } = await supabase.functions.invoke('create-job', {
      body: jobData,
    });

    if (error) {
      console.error('[Edge Function Error] createJob:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: data?.data || null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] createJob:', err);
    return { data: null, error: err };
  }
}

export async function updateJobWithEdgeFunction(jobId: string, jobData: Partial<CreateJobPayload>): Promise<ApiResponse<Job>> {
  try {
    const { data, error } = await supabase.functions.invoke('update-job', {
      body: {
        job_id: jobId,
        ...jobData,
      },
    });

    if (error) {
      console.error('[Edge Function Error] updateJob:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: data?.data || null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] updateJob:', err);
    return { data: null, error: err };
  }
}

export async function updateJobStatusWithEdgeFunction(jobId: string, status: string): Promise<ApiResponse<null>> {
  try {
    const { data, error } = await supabase.functions.invoke('update-job-status', {
      body: {
        job_id: jobId,
        status,
      },
    });

    if (error) {
      console.error('[Edge Function Error] updateJobStatus:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] updateJobStatus:', err);
    return { data: null, error: err };
  }
}

export async function updateJobPocWithEdgeFunction(jobId: string, newPocId: string): Promise<ApiResponse<Job>> {
  try {
    const { data, error } = await supabase.functions.invoke('update-job-poc', {
      body: {
        job_id: jobId,
        new_poc_id: newPocId,
      },
    });

    if (error) {
      console.error('[Edge Function Error] updateJobPoc:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: data?.data || null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] updateJobPoc:', err);
    return { data: null, error: err };
  }
}

export async function deleteJobWithEdgeFunction(jobId: string): Promise<ApiResponse<null>> {
  try {
    const { data, error } = await supabase.functions.invoke('delete-job', {
      body: {
        job_id: jobId,
      },
    });

    if (error) {
      console.error('[Edge Function Error] deleteJob:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] deleteJob:', err);
    return { data: null, error: err };
  }
}
